
import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import { CategoryBadge } from "@/components/CategoryBadge";
import { RelatedBlogs } from "@/components/RelatedBlogs";
import { PageTitle } from "@/components/PageTitle";

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const post = blogPosts.find((post) => post.slug === slug);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (!post) {
      navigate("/blog", { replace: true });
    }
  }, [post, navigate]);
  
  if (!post) return null;

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = "/placeholder.svg";
  };
  
  return (
    <div className="flex flex-col min-h-screen">
      <PageTitle
        title={post.metaTitle || `${post.title} - Asrani Dental Clinic`}
        description={post.metaDescription || post.excerpt}
        ogTitle={post.ogTitle || post.metaTitle || post.title}
        ogDescription={post.ogDescription || post.metaDescription || post.excerpt}
        ogUrl={typeof window !== 'undefined' ? window.location.href : undefined}
        ogImage={post.ogImage || post.coverImage || post.featuredImage}
      />
      <Header />
      <main className="flex-grow pt-24 lg:pt-28 bg-dental-offwhite">
        <article className="container-custom py-12 md:py-16">
          <Link to="/blog" className="inline-flex items-center text-dental-highlight hover:underline mb-6">
            <ArrowLeft size={18} className="mr-1" />
            Back to all blogs
          </Link>
          
          <div className="max-w-4xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-soft">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <CategoryBadge category={post.category} />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-serif font-medium text-dental-text mb-4">{post.title}</h1>
            
            <div className="flex flex-wrap items-center text-dental-muted text-sm mb-6 gap-4">
              <div className="flex items-center">
                <Calendar size={16} className="mr-1" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center">
                <Clock size={16} className="mr-1" />
                <span>{post.readTime} min read</span>
              </div>
              <div className="flex items-center">
                <span className="text-dental-muted">By {post.author}</span>
              </div>
            </div>
            
            {(post.coverImage || post.featuredImage) && (
              <div className="mb-8 rounded-lg overflow-hidden shadow-md">
                <img 
                  src={post.coverImage || post.featuredImage || "/placeholder.svg"}
                  alt={`Cover image for ${post.title}`}
                  className="w-full h-auto max-h-96 object-cover rounded-lg"
                  onError={handleImageError}
                  loading="eager"
                />
              </div>
            )}
            
            <div 
              className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-dental-text 
              prose-p:text-dental-text/90 prose-p:leading-relaxed prose-p:mb-6 
              prose-a:text-dental-highlight prose-a:no-underline hover:prose-a:underline
              prose-li:text-dental-text/80 prose-li:my-1 prose-li:leading-relaxed
              prose-strong:text-dental-text prose-strong:font-medium
              prose-ul:my-6 prose-ul:pl-5 prose-ol:my-6 prose-ol:pl-5
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-h4:text-lg prose-h4:mt-4 prose-h4:mb-2
              prose-h5:text-base prose-h5:mt-3 prose-h5:mb-2" 
              dangerouslySetInnerHTML={{ __html: post.content }} 
            />
            
            <div className="mt-10 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <span className="text-dental-muted text-sm">Written by</span>
                  <p className="font-medium text-dental-text">{post.author}</p>
                  <p className="text-dental-muted text-sm">Published on {post.date}</p>
                </div>
                
                {/* Social Sharing Icons */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:opacity-80 transition-opacity"
                    aria-label="Share on Facebook"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.677 20.895v-7.745H7.687v-2.825h1.99V8.248c0-2.925 1.807-4.248 4.145-4.248 1.18 0 2.192.088 2.485.127v2.888l-1.707.001c-1.336 0-1.594.633-1.594 1.561v2.065h3.19l-.416 2.825h-2.774v7.745" />
                    </svg>
                  </button>
                  <button
                    onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${window.location.href}`, '_blank')}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1DA1F2] text-white hover:opacity-80 transition-opacity"
                    aria-label="Share on Twitter"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${encodeURIComponent(post.title)}`, '_blank')}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0A66C2] text-white hover:opacity-80 transition-opacity"
                    aria-label="Share on LinkedIn"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      // Simple toast notification
                      const toast = document.createElement('div');
                      toast.textContent = 'Link copied to clipboard!';
                      toast.className = 'fixed top-4 right-4 bg-dental-highlight text-white px-4 py-2 rounded-lg shadow-lg z-50';
                      document.body.appendChild(toast);
                      setTimeout(() => document.body.removeChild(toast), 3000);
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-500 text-white hover:opacity-80 transition-opacity"
                    aria-label="Copy link"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <RelatedBlogs currentPostId={post.id} />
        </article>
      </main>
      <Footer />
    </div>
  );
};
